/**
 * Calendar integration domain — React Query hooks.
 *
 * Read hooks are pass-through re-exports from the generated controller.
 * Mutations are wrapped through `useInvalidatingMutation` so the typed
 * `invalidationRegistry` runs after each successful call.
 */

import { selectCalendar } from '../generated/clients/selectCalendar'
import { syncCalendar } from '../generated/clients/syncCalendar'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

import type { SelectCalendarPathParams } from '../generated/types/SelectCalendar'
import type { SyncCalendarPathParams } from '../generated/types/SyncCalendar'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListCalendars,
  listCalendarsQueryKey,
  useListCalendarEvents,
  listCalendarEventsQueryKey,
} from '../generated/hooks/calendarIntegrationController'

// ── Generated parameter & response type re-exports ─────────────────

export type {
  ListCalendarsQueryResponse,
  ListCalendarsPathParams,
} from '../generated/types/ListCalendars'
export type {
  ListCalendarEventsQueryResponse,
  ListCalendarEventsQueryParams,
  ListCalendarEventsPathParams,
} from '../generated/types/ListCalendarEvents'
export type {
  SelectCalendarMutationResponse,
  SelectCalendarPathParams,
} from '../generated/types/SelectCalendar'
export type {
  SyncCalendarMutationResponse,
  SyncCalendarPathParams,
} from '../generated/types/SyncCalendar'

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useSelectCalendar() {
  return useInvalidatingMutation('selectCalendar', invalidationRegistry, {
    mutationFn: (variables: {
      integration_id: SelectCalendarPathParams['integration_id']
      id: SelectCalendarPathParams['id']
    }) =>
      selectCalendar({
        integration_id: variables.integration_id,
        id: variables.id,
      }),
  })
}

export function useSyncCalendar() {
  return useInvalidatingMutation('syncCalendar', invalidationRegistry, {
    mutationFn: (variables: {
      integration_id: SyncCalendarPathParams['integration_id']
    }) => syncCalendar({ integration_id: variables.integration_id }),
  })
}
